import { useQuery, useMutation } from 'react-query';
import axios from 'axios';
import { api } from 'services/api';

type TranscriptionChunk = {
  text: string;
  usage?: { type: 'duration'; seconds: number };
};

type TranscriptionResult = {
  text: string;         
  seconds: number | null;
  raw: TranscriptionChunk[];
};

type UseTranscriptionParams = {
  webhookId: string;
  audioUrl: string; 
  enabled?: boolean;
};

const TRANSCRIBE_BASE =
  'https://n8n.tecimob.com.br/webhook';

async function fetchTranscription(
  webhookId: string,
  audioUrl: string
): Promise<TranscriptionResult> {
  const url = `${TRANSCRIBE_BASE}/${webhookId}`;
  const { data } = await axios.get<TranscriptionChunk[]>(url, {
    params: { audio: audioUrl },
  });

  const chunks = Array.isArray(data) ? data : [];
  const text = chunks.map(c => c.text).join(' ').trim();
  const seconds = chunks.reduce(
    (acc, c) => acc + (c.usage?.seconds ?? 0),
    0
  );
  return {
    text,
    seconds: Number.isFinite(seconds) ? seconds : null,
    raw: chunks,
  };
}

export function useTranscription({
  webhookId,
  audioUrl,
  enabled = true,
}: UseTranscriptionParams) {
  return useQuery({
    queryKey: ['transcription', webhookId, audioUrl],
    queryFn: () => fetchTranscription(webhookId, audioUrl),
    enabled: Boolean(webhookId && audioUrl && enabled),
  });
}

type AnnotatePayload = {
  body: string;
  contactId: string;
  quotedMsgId: string;
  isAnnotation?: boolean;
  isCopyDate?: boolean;
};

type TranscribePayload = {
  messageId: string;
  transcribed_text: string;
};

type AnnotateResponse = unknown;

const ANNOTATE_URL =
  'https://whats-central-api.tecimob.com.br/api/messages/send-text';

async function postAnnotation(payload: AnnotatePayload): Promise<AnnotateResponse> {
  const { data } = await api.post(ANNOTATE_URL, {
    body: payload.body,
    contactId: payload.contactId,
    isAnnotation: payload.isAnnotation ?? true,
    quotedMsgId: payload.quotedMsgId,
    isCopyDate: payload.isCopyDate ?? true,
  });
  return data;
}

async function saveTranscribedText(payload: TranscribePayload): Promise<AnnotateResponse> {
  const { data } = await api.put('/messages/'+payload.messageId+'/save-transcribed-text', {
    transcribed_text: payload.transcribed_text,
  });
  return data;
}

export function useAnnotateMessage() {
  return useMutation({
    mutationFn: postAnnotation,
  });
}

export function useTranscribedMessage() {
  return useMutation({
    mutationFn: saveTranscribedText,
  });
}

type TranscribeAndAnnotateArgs = {
  webhookId: string;
  audioUrl: string;
  contactId: string;
  quotedMsgId: string;
  buildBody?: (transcription: TranscriptionResult) => string;
  isCopyDate?: boolean;
};

export function useTranscribeAndAnnotate() {
  return useMutation({
    mutationFn: async ({
      webhookId,
      audioUrl,
      contactId,
      quotedMsgId,
      buildBody,
      isCopyDate,
    }: TranscribeAndAnnotateArgs) => {
      const transcription = await fetchTranscription(webhookId, audioUrl);

      const body =
        buildBody?.(transcription) ??
        (transcription.text || '');

      if (!body) {
        throw new Error('Transcrição vazia — nada para anotar.');
      }

      const annotationRes = await postAnnotation({
        body,
        contactId,
        quotedMsgId,
        isAnnotation: true,
        isCopyDate: isCopyDate ?? true,
      });

      return {
        transcription,
        annotationRes,
      };
    },
  });
}
